import { APP_MODE_TYPES, appMode } from 'src/config'
import { CONDITIONS, CONDITIONS_REQUIREMENTS, TYPES } from 'src/models/booking-fee-type/BookingFeeType'
import { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'

/**
 * Computes the highest priority applicable BookingFeeType for an Order.
 */
class BookingFeesOrderComputer {
  static #instance = null

  constructor () {
    if (!BookingFeesOrderComputer.#instance) {
      BookingFeesOrderComputer.#instance = this
    }
    return BookingFeesOrderComputer.#instance
  }

  /**
   * Compute the highest priority BookingFeeType for the given Order,
   * based on the Order's state and any conditions configured for each BookingFeeType.
   * @param {Order} order
   * @returns {BookingFeeType} validBookingFeeType
   */
  compute (order) {
    if (order.orderItems.data.length === 0) return null

    const orderItemTypes = _(order.orderItems.data).uniqBy('type').map('type').compact().value()
    if (!_.some(orderItemTypes, orderItemType => _.includes(ORDER_ITEM_TYPES.BOOKING_FEE_CONDITION, orderItemType))) {
      // Order must include applicable order items with either a Ticket or Item for a booking fee to apply
      // e.g. this will exclude Membership Orders
      return null
    }

    const bookingFeeTypes = _.sortBy(order.configuration?.bookingFeeTypes?.data, 'priorityOrder')

    const validBookingFeeType = _.find(bookingFeeTypes, bookingFeeType => {
      if (bookingFeeType.type !== TYPES.ORDER) return false
      if (!bookingFeeType.isActive) return false

      const channel = this.#channel(order)
      if (!bookingFeeType.channels[channel]) return false

      if (bookingFeeType.conditions[CONDITIONS.PRICE]) {
        const price = _.sumBy(order.orderItems.data, orderItem => {
          if (orderItem.type === ORDER_ITEM_TYPES.BOOKING_FEE && orderItem.subtype === TYPES.ORDER) {
            return 0
          } else if (orderItem.setAsGross) {
            return orderItem.total
          } else {
            return orderItem.subtotal
          }
        })

        const atLeastMinPrice = price >= bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.MIN_PRICE]
        const atMostMaxPrice = _.isNil(bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.MAX_PRICE]) ||
                              price <= bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.MAX_PRICE]
        const includesPrice = atLeastMinPrice && atMostMaxPrice

        if (bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_PRICE] === includesPrice) return false
      }

      if (bookingFeeType.conditions[CONDITIONS.DELIVERY_LOCATIONS]) {
        const orderDeliveryLocationId = order.effectiveDeliveryLocationModel?.id

        if (bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_DELIVERY_LOCATIONS] === _.includes(bookingFeeType.deliveryLocationIds, orderDeliveryLocationId)) return false
      }

      if (bookingFeeType.conditions[CONDITIONS.MEMBERSHIP_TYPES]) {
        if (bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_MEMBERSHIP_TYPES] === _.includes(bookingFeeType.membershipTypeIds, order.user?.effectiveMembershipType?.id)) return false
      }

      if (bookingFeeType.conditions[CONDITIONS.MEMBERSHIP_TIERS]) {
        if (bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_MEMBERSHIP_TIERS] === _.includes(bookingFeeType.membershipTierIds, order.user?.membershipTier?.id)) return false
      }

      if (bookingFeeType.conditions[CONDITIONS.ORDER_ITEM_TYPES]) {
        const includesAllOrderItemTypes = _.difference(bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.ORDER_ITEM_TYPES], orderItemTypes).length === 0
        if (bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_ORDER_ITEM_TYPES] === includesAllOrderItemTypes) return false
      }

      if (bookingFeeType.conditions[CONDITIONS.SHOWING_BADGES]) {
        // Fail the condition if no showing passes the condition
        if (!_.some(order.allShowings, showing => {
          const includesAllShowingBadges = _.difference(bookingFeeType.showingBadgeIds, showing.showingBadgeIds).length === 0
          // Return true for showings PASSING the showing badge condition (do not continue testing showings if one passes)
          // Other conditions test for FAILING the condition, because we want to exit without testing other conditions as soon as one fails
          return bookingFeeType.conditionsRequirements[CONDITIONS_REQUIREMENTS.EXCLUDE_SHOWING_BADGES] !== includesAllShowingBadges
        })) return false
      }

      return true
    })

    return validBookingFeeType
  }

  // private

  /**
   * Gets the appropriate channel for the given Order.
   * The channel is determined by the being application used to place the given Order.
   * @param {Order} order
   * @returns {String} channel
   */
  #channel (order) {
    let channel

    switch (appMode) {
      case APP_MODE_TYPES.REGISTER:
        channel = order.phoneOrder ? 'phone' : 'register'
        break
      case APP_MODE_TYPES.CONSUMER:
        channel = 'website'
        break
      default:
        channel = appMode
    }

    return channel
  }
}

const orderBookingFeeComputer = new BookingFeesOrderComputer()
export default orderBookingFeeComputer
